import { useEffect, useRef, useState, type ReactNode } from "react";

interface FilterMarqueeProps {
  dir: "rtl" | "ltr";
  paused: boolean;
  children: ReactNode;
}

export function FilterMarquee({ dir, paused, children }: FilterMarqueeProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startScroll: 0, moved: false });
  const wasDraggedRef = useRef(false);

  useEffect(() => {
    if (paused || isDragging) return;
    const el = scrollRef.current;
    if (!el) return;
    let raf: number;
    const speed = 0.4;
    const tick = () => {
      const half = el.scrollWidth / 2;
      if (dir === "rtl") {
        el.scrollLeft -= speed;
        if (el.scrollLeft <= 0) el.scrollLeft = half;
      } else {
        el.scrollLeft += speed;
        if (el.scrollLeft >= half) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, isDragging, dir]);

  return (
    <div
      ref={scrollRef}
      dir="ltr"
      className="flex gap-2 overflow-x-hidden scrollbar-hide pb-1 -mx-5 px-5 cursor-grab active:cursor-grabbing select-none"
      style={{ touchAction: "none" }}
      onClickCapture={(e) => {
        if (wasDraggedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          wasDraggedRef.current = false;
        }
      }}
      onPointerDown={(e) => {
        setIsDragging(true);
        dragRef.current = {
          startX: e.clientX,
          startScroll: scrollRef.current?.scrollLeft ?? 0,
          moved: false,
        };
      }}
      onPointerMove={(e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragRef.current.startX;
        if (Math.abs(dx) > 5) dragRef.current.moved = true;
        if (scrollRef.current)
          scrollRef.current.scrollLeft = dragRef.current.startScroll - dx;
      }}
      onPointerUp={() => {
        if (dragRef.current.moved) wasDraggedRef.current = true;
        setIsDragging(false);
      }}
      onPointerLeave={() => setIsDragging(false)}
    >
      <div className="flex gap-2 shrink-0">{children}</div>
      <div className="flex gap-2 shrink-0" aria-hidden>
        {children}
      </div>
    </div>
  );
}
